import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { TwoFactorGate, twoFactorError } from "@/components/TwoFactor";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Copy, Play, ChevronDown, ChevronRight, Database, AlertTriangle, RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";

const stripExt = (f) => f.replace(/\.sql\.zst(\.age|\.gpg)?$/i, "");

export default function Restore() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [sources, setSources] = useState([]);
  const [tests, setTests] = useState([]);
  const [selectedRun, setSelectedRun] = useState("");
  const [instructions, setInstructions] = useState(null);
  const [selectedDbs, setSelectedDbs] = useState([]);
  const [destMode, setDestMode] = useState("target"); // "target" | "original"
  const [target, setTarget] = useState("");
  const [targetDb, setTargetDb] = useState("restore_verify");
  const [confirmName, setConfirmName] = useState("");
  const [dangerAck, setDangerAck] = useState(false);
  const [privKey, setPrivKey] = useState("");
  const [expectedDbs, setExpectedDbs] = useState("");
  const [showCli, setShowCli] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [conflictPolicy, setConflictPolicy] = useState("fail");

  const loadTests = () => api.get("/restore/tests").then((r) => setTests(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/runs?status=succeeded").then((r) => setRuns(r.data)).catch(() => {});
    api.get("/sources").then((r) => setSources(r.data)).catch(() => {});
    loadTests();
    const iv = setInterval(loadTests, 4000);
    return () => clearInterval(iv);
  }, []);

  const runObj = runs.find((r) => r.id === selectedRun);
  const isPhysical = !!instructions?.steps || runObj?.engine === "physical";
  const encrypted = !!instructions?.encrypted;
  const encMethod = instructions?.encryption_method;
  const dataOnly = !isPhysical && (instructions?.data_only ?? runObj?.dump_options?.includes("--no-create-info"));
  const keepExisting = conflictPolicy === "keep_existing" && dataOnly;
  const targetSource = sources.find((s) => s.id === (destMode === "original" ? runObj?.source_id : target));
  const needsOtp = !isPhysical && targetSource?.write_protected !== false;

  const dbFiles = useMemo(
    () => (instructions?.commands || []).map((c) => c.file),
    [instructions]
  );
  const sourceName = useMemo(() => {
    if (!runObj) return "";
    return sources.find((s) => s.id === runObj.source_id)?.name || runObj.source_name || "";
  }, [runObj, sources]);

  const pickRun = async (runId) => {
    setSelectedRun(runId);
    setInstructions(null);
    setSelectedDbs([]);
    setDestMode("target");
    setConfirmName("");
    setDangerAck(false);
    setPrivKey("");
    setConflictPolicy("fail");
    try {
      const r = await api.post("/restore/instructions", { run_id: runId });
      setInstructions(r.data);
      const selected = runs.find((run) => run.id === runId);
      setConflictPolicy((r.data.data_only ?? selected?.dump_options?.includes("--no-create-info")) ? "keep_existing" : "fail");
      setSelectedDbs((r.data.commands || []).map((c) => c.file));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("restore.failed"));
    }
  };

  const toggleDb = (file) =>
    setSelectedDbs((prev) =>
      prev.includes(file) ? prev.filter((f) => f !== file) : [...prev, file]);

  const allSelected = dbFiles.length > 0 && selectedDbs.length === dbFiles.length;
  const toggleAll = () => setSelectedDbs(allSelected ? [] : [...dbFiles]);

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success(t("restore.copied")); };

  const canRestore =
    (user?.role === "admin" || user?.role === "operator") &&
    !!selectedRun &&
    (isPhysical
      ? true
      : selectedDbs.length > 0 &&
        (encrypted ? !!privKey : true) &&
        (destMode === "target"
          ? !!target
          : confirmName.trim() === sourceName && dangerAck));

  const runRestore = async () => {
    if (!canRestore || submitting) return;
    if (needsOtp) { setGateOpen(true); return; }
    await executeRestore();
  };

  const executeRestore = async (otp) => {
    if (!canRestore || submitting) return;
    if (!selectedRun) { toast.error(t("restore.selectRunFirst")); return; }
    setSubmitting(true);
    try {
      if (isPhysical) {
        await api.post("/restore/test", {
          run_id: selectedRun, restore_mode: "auto", kind: "test",
          expected_databases: expectedDbs.split(",").map((x) => x.trim()).filter(Boolean),
          age_private_key: encMethod === "age" ? (privKey || undefined) : undefined,
          gpg_private_key: encMethod === "gpg" ? (privKey || undefined) : undefined,
        });
        toast.success(t("restore.restoreStarted"));
      } else {
        if (selectedDbs.length === 0) { toast.error(t("restore.selectDbFirst")); return; }
        const tgtId = destMode === "original" ? runObj.source_id : target;
        if (destMode === "target" && !tgtId) { toast.error(t("restore.selectTargetDb")); return; }
        if (destMode === "original" && (confirmName.trim() !== sourceName || !dangerAck)) {
          toast.error(t("restore.confirmMismatch")); return;
        }
        await api.post("/restore/test", {
          run_id: selectedRun, kind: "restore",
          target_source_id: tgtId, target_database: targetDb || "restore_verify",
          selected_files: selectedDbs, validation_queries: [],
          conflict_policy: keepExisting ? "keep_existing" : "fail",
          age_private_key: encMethod === "age" ? (privKey || undefined) : undefined,
          gpg_private_key: encMethod === "gpg" ? (privKey || undefined) : undefined,
          confirm: destMode === "original",
          otp,
        });
        toast.success(t("restore.restoreStartedReal"));
      }
      setGateOpen(false);
      loadTests();
    } catch (e) {
      toast.error(twoFactorError(e, t, "restore.failed"));
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("Set up 2FA")) setGateOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="restore-page">
      <TwoFactorGate open={gateOpen} onOpenChange={setGateOpen} busy={submitting}
        title={t("twofa.restoreTitle")} description={t("twofa.restoreDesc", { name: targetSource?.name || sourceName })}
        onVerified={executeRestore} />
      <PageHeader title={t("restore.title")} subtitle={t("restore.subtitleNew")} />
      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: pick backup + what to restore + CLI */}
        <div className="space-y-6">
          <div className="grid-panel p-5 space-y-3">
            <Label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{t("restore.selectBackup")}</Label>
            <Select value={selectedRun} onValueChange={pickRun}>
              <SelectTrigger className="rounded-none" data-testid="restore-run-select"><SelectValue placeholder={t("restore.chooseRun")} /></SelectTrigger>
              <SelectContent>
                {runs.map((r) => <SelectItem key={r.id} value={r.id}>{r.engine === "physical" ? "⬛ " : ""}{r.plan_name} · {r.backup_type || "full"} · {fmtDate(r.created_at)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* What to restore */}
          {selectedRun && (
            <div className="grid-panel" data-testid="restore-what">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                <h3 className="font-display font-bold flex items-center gap-2"><Database className="h-4 w-4 text-brand" />{t("restore.whatToRestore")}</h3>
                {encrypted && <StatusBadge status="yes" label={t("restore.encrypted")} />}
              </div>
              <div className="p-5 space-y-3">
                {isPhysical ? (
                  <p className="text-sm text-muted-foreground">{t("restore.physicalWhole")}</p>
                ) : dbFiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("restore.loadingDbs")}</p>
                ) : (
                  <>
                    <button type="button" onClick={toggleAll} className="text-xs font-mono uppercase text-brand hover:underline" data-testid="restore-toggle-all">
                      {allSelected ? t("restore.deselectAll") : t("restore.selectAll")}
                    </button>
                    <div className="divide-y divide-border border border-border">
                      {dbFiles.map((f) => (
                        <label key={f} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary" data-testid={`restore-db-${stripExt(f)}`}>
                          <Checkbox checked={selectedDbs.includes(f)} onCheckedChange={() => toggleDb(f)} className="rounded-none" />
                          <span className="font-mono text-sm">{stripExt(f)}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">{t("restore.selectedCount", { n: selectedDbs.length, total: dbFiles.length })}</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* CLI fallback (collapsible) */}
          {instructions && (
            <div className="grid-panel" data-testid="restore-cli">
              <button type="button" onClick={() => setShowCli((v) => !v)} className="w-full px-5 py-3 border-b border-border flex items-center justify-between" data-testid="restore-cli-toggle">
                <h3 className="font-display font-bold flex items-center gap-2">
                  {showCli ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {t("restore.advancedCli")}
                </h3>
              </button>
              {showCli && (
                <div className="p-5 space-y-4">
                  {!isPhysical && <p className="text-xs text-yellow-700">{t("restore.cliPolicyHint")}</p>}
                  <div className="space-y-1">
                    <p className="font-mono text-[11px] uppercase text-muted-foreground">{t("restore.verifyChecksum")}</p>
                    <CodeBlock text={instructions.verify_checksum} onCopy={copy} />
                  </div>
                  {(instructions.steps || []).length > 0 && (
                    <div className="space-y-1">
                      <p className="font-mono text-[11px] uppercase text-muted-foreground">{t("restore.restoreSteps")}</p>
                      <CodeBlock text={instructions.steps.join("\n")} onCopy={copy} />
                    </div>
                  )}
                  {(instructions.commands || []).map((c, i) => (
                    <div key={i} className="space-y-1">
                      <p className="font-mono text-[11px] uppercase text-muted-foreground">{c.file}</p>
                      <CodeBlock text={c.restore} onCopy={copy} />
                    </div>
                  ))}
                  <ul className="text-xs text-yellow-700 list-disc list-inside space-y-1">
                    {(instructions.warnings || []).map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: restore destination + execute + history */}
        <div className="space-y-6">
          <div className="grid-panel p-5 space-y-4" data-testid="restore-destination">
            <h3 className="font-display font-bold">{isPhysical ? t("restore.runRestoreTest") : t("restore.destination")}</h3>

            {isPhysical ? (
              <>
                <p className="text-xs text-muted-foreground">{t("restore.physicalDesc")}</p>
                <Field label={t("restore.expectedDbs")}>
                  <Input value={expectedDbs} onChange={(e) => setExpectedDbs(e.target.value)} className="rounded-none" data-testid="restore-expected-dbs" placeholder="demo_shop, demo_blog" />
                </Field>
              </>
            ) : (
              <>
                {/* destination mode segmented control */}
                <div className="grid grid-cols-2 gap-px bg-border border border-border" data-testid="restore-dest-mode">
                  <button type="button" onClick={() => setDestMode("target")} data-testid="restore-mode-target"
                    className={cn("py-2.5 text-xs font-mono uppercase tracking-wide transition-colors", destMode === "target" ? "bg-[#0A0A0A] text-white" : "bg-card hover:bg-secondary")}>
                    {t("restore.destSafe")}
                  </button>
                  <button type="button" onClick={() => setDestMode("original")} data-testid="restore-mode-original"
                    className={cn("py-2.5 text-xs font-mono uppercase tracking-wide transition-colors", destMode === "original" ? "bg-destructive text-white" : "bg-card hover:bg-secondary")}>
                    {t("restore.destOriginal")}
                  </button>
                </div>

                {destMode === "target" ? (
                  <>
                    <Field label={t("restore.targetServer")}>
                      <Select value={target} onValueChange={setTarget}>
                        <SelectTrigger className="rounded-none" data-testid="restore-target-select"><SelectValue placeholder={t("restore.chooseTarget")} /></SelectTrigger>
                        <SelectContent>{sources.filter((s) => s.source_mode !== "ssh_mariadb_host").map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label={t("restore.targetDbName")}>
                      <Input value={targetDb} onChange={(e) => setTargetDb(e.target.value)} className="rounded-none" data-testid="restore-target-db" />
                      <p className="text-[11px] text-muted-foreground mt-1">{t("restore.targetDbHelp")}</p>
                    </Field>
                  </>
                ) : (
                  <div className="border border-destructive/50 bg-destructive/5 p-4 space-y-3" data-testid="restore-danger">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <p className="font-bold text-destructive">{t(keepExisting ? "restore.mergeOriginalTitle" : "restore.dangerTitle")}</p>
                        <p className="text-muted-foreground mt-1">{t(keepExisting ? "restore.mergeOriginalBody" : "restore.dangerBody", { name: sourceName || "—" })}</p>
                      </div>
                    </div>
                    <Field label={t("restore.typeToConfirm", { name: sourceName || "—" })}>
                      <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} className="rounded-none border-destructive/50" data-testid="restore-confirm-name" placeholder={t("restore.typeToConfirmPlaceholder")} />
                    </Field>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{t(keepExisting ? "restore.mergeOriginalAck" : "restore.dangerToggle")}</Label>
                      <Switch checked={dangerAck} onCheckedChange={setDangerAck} data-testid="restore-confirm" />
                    </div>
                  </div>
                )}
                {instructions && (
                  <Field label={t("restore.conflictPolicy")}>
                    <Select value={keepExisting ? "keep_existing" : "fail"} onValueChange={(value) => {
                      setConflictPolicy(value);
                      setDangerAck(false);
                      setGateOpen(false);
                    }}>
                      <SelectTrigger className="rounded-none" data-testid="restore-conflict-policy"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {dataOnly && <SelectItem value="keep_existing">{t("restore.keepExisting")}</SelectItem>}
                        <SelectItem value="fail">{t("restore.dumpPolicy")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-2" data-testid="restore-policy-help">
                      {t(keepExisting ? "restore.keepExistingHelp" : "restore.dumpPolicyHelp")}
                    </p>
                    {!dataOnly && <p className="text-xs text-muted-foreground mt-1">{t("restore.mergeDataOnly")}</p>}
                  </Field>
                )}
              </>
            )}

            {encrypted && (
              <Field label={t("restore.privKeyLabel", { method: encMethod })}>
                <textarea value={privKey} onChange={(e) => setPrivKey(e.target.value)} rows={2} className="w-full rounded-none border border-input p-2 font-mono text-xs" data-testid="restore-privkey" />
              </Field>
            )}

            <Button
              className={cn("w-full rounded-none uppercase",
                destMode === "original" && !isPhysical ? "bg-destructive hover:bg-destructive/90" : "bg-[#0A0A0A] hover:bg-[#0052FF]")}
              onClick={runRestore}
              disabled={!canRestore || submitting}
              data-testid="run-restore-test"
            >
              {destMode === "original" && !isPhysical ? <RotateCcw className="h-4 w-4 mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}
              {isPhysical ? t("restore.runRestoreBtn")
                : destMode === "original" ? t(keepExisting ? "restore.mergeOriginalButton" : "restore.restoreNowDanger") : t("restore.restoreNow")}
            </Button>
          </div>

          {/* History */}
          <div className="grid-panel">
            <div className="px-5 py-3 border-b border-border"><h3 className="font-display font-bold">{t("restore.recentRestores")}</h3></div>
            {tests.length === 0 ? <div className="px-5 py-8 text-sm text-muted-foreground text-center">{t("restore.noRestores")}</div> : (
              <ul className="divide-y divide-border">
                {tests.map((tt) => (
                  <li key={tt.id} className="px-5 py-3" data-testid={`restore-test-${tt.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={cn("font-mono text-[10px] uppercase px-1.5 py-0.5", tt.kind === "restore" ? "bg-[#0052FF] text-white" : "bg-secondary text-muted-foreground")}>
                            {tt.kind === "restore" ? t("restore.kindRestore") : t("restore.kindTest")}
                          </span>
                          {tt.to_original_source && <span className="font-mono text-[10px] uppercase text-destructive">{t("restore.toOriginal")}</span>}
                        </div>
                        <span className="font-mono text-xs text-muted-foreground block mt-1 truncate">
                          {(tt.target_source_name || tt.target_database || "")} · {fmtDate(tt.created_at)}
                        </span>
                      </div>
                      <StatusBadge status={tt.status} />
                    </div>
                    {tt.merge_results?.length > 0 && (
                      <div className="mt-2 text-xs" data-testid={`restore-merge-${tt.id}`}>
                        <p>{t("restore.mergeSummary", {
                          inserted: tt.merge_results.reduce((sum, row) => sum + row.inserted, 0),
                          skipped: tt.merge_results.reduce((sum, row) => sum + row.skipped, 0),
                        })}</p>
                        {tt.merge_results.map((row) => (
                          <p key={`${row.database}.${row.table}`} className="text-muted-foreground">
                            {row.database}.{row.table} · {t("restore.mergeSummary", row)}
                          </p>
                        ))}
                      </div>
                    )}
                    {tt.status === "failed" && /(?:ERROR 1062|Duplicate entry)/i.test(tt.log || "") && (
                      <p className="mt-2 text-xs text-destructive" data-testid={`restore-duplicate-${tt.id}`}>{t("restore.duplicateHint")}</p>
                    )}
                    {tt.log && <pre className="mt-2 font-mono text-[10px] bg-secondary p-2 overflow-x-auto max-h-40 whitespace-pre-wrap">{tt.log}</pre>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CodeBlock({ text, onCopy }) {
  return (
    <div className="flex items-start gap-2 bg-[#0A0A0A] p-3">
      <code className="flex-1 font-mono text-xs text-green-400 break-all whitespace-pre-wrap">{text}</code>
      <button onClick={() => onCopy(text)} className="text-white/50 hover:text-white shrink-0"><Copy className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
